import React from 'react';
import { CheckCircle2, Circle } from 'lucide-react';

// ── Criteria ─────────────────────────────────────────────────────────────────
export const PASSWORD_CRITERIA = [
  { id: 'length',    label: 'At least 8 characters',          test: (pw) => pw.length >= 8 },
  { id: 'uppercase', label: 'At least one uppercase letter',   test: (pw) => /[A-Z]/.test(pw) },
  { id: 'number',    label: 'At least one number',             test: (pw) => /[0-9]/.test(pw) },
  { id: 'special',   label: 'At least one special character',  test: (pw) => /[^A-Za-z0-9]/.test(pw) },
];

/** Returns true only when all 4 criteria are satisfied */
export const isPasswordStrong = (pw) =>
  PASSWORD_CRITERIA.every((c) => c.test(pw));

// ── Strength config ───────────────────────────────────────────────────────────
const STRENGTH_CONFIG = [
  { label: 'Very Weak', barColor: 'bg-red-500',    textColor: 'text-red-500' },   // 0
  { label: 'Weak',      barColor: 'bg-red-400',    textColor: 'text-red-500' },   // 1
  { label: 'Fair',      barColor: 'bg-orange-400', textColor: 'text-orange-500' },// 2
  { label: 'Good',      barColor: 'bg-yellow-400', textColor: 'text-yellow-600' },// 3
  { label: 'Strong',    barColor: 'bg-green-500',  textColor: 'text-green-600' },  // 4
];

// ── Component ─────────────────────────────────────────────────────────────────
const PasswordStrengthIndicator = ({ password }) => {
  if (!password) return null;

  const score  = PASSWORD_CRITERIA.filter((c) => c.test(password)).length;
  const config = STRENGTH_CONFIG[score];

  return (
    <div className="mt-2.5 space-y-2">
      {/* Segmented bar */}
      <div className="flex items-center gap-1.5">
        <div className="flex flex-1 gap-1">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${
                i < score ? config.barColor : 'bg-gray-200'
              }`}
            />
          ))}
        </div>
        <span className={`text-xs font-semibold shrink-0 ${config.textColor}`}>
          {config.label}
        </span>
      </div>

      {/* Criteria checklist */}
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
        {PASSWORD_CRITERIA.map((c) => {
          const met = c.test(password);
          return (
            <li
              key={c.id}
              className={`flex items-center gap-1.5 text-xs transition-colors ${
                met ? 'text-green-600' : 'text-brand-muted'
              }`}
            >
              {met ? (
                <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
              ) : (
                <Circle className="w-3.5 h-3.5 shrink-0" />
              )}
              {c.label}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default PasswordStrengthIndicator;
